import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tablesDB, DB_ID, ORDERS_TABLE, CART_TABLE } from "../../appwrite";
import { Query } from "appwrite";
import "./AdminOrDet.css";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Truck,
  XCircle,
  Package,
  User,
  Phone,
  Save,
} from "lucide-react";
import { toast } from "react-toastify";

const fmtPrice = (v) =>
  "₦" +
  parseFloat(v || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const fmtDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const parseInfoArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const r = {};
  arr.forEach((item) => {
    if (typeof item === "string") {
      try {
        Object.assign(r, JSON.parse(item));
      } catch {
        const i = item.indexOf(":");
        if (i > -1) r[item.slice(0, i).trim()] = item.slice(i + 1).trim();
      }
    }
  });
  return r;
};

const STATUSES = [
  {
    value: "pending",
    label: "Pending",
    icon: Clock,
    cls: "det-status--pending",
  },
  {
    value: "processing",
    label: "Processing",
    icon: Package,
    cls: "det-status--processing",
  },
  {
    value: "out-for-delivery",
    label: "Out for Delivery",
    icon: Truck,
    cls: "det-status--shipping",
  },
  {
    value: "delivered",
    label: "Delivered",
    icon: CheckCircle,
    cls: "det-status--delivered",
  },
  {
    value: "cancelled",
    label: "Cancelled",
    icon: XCircle,
    cls: "det-status--cancelled",
  },
];

export default function AdminOrDet() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rider, setRider] = useState({ name: "", phone: "" });
  const [savingRider, setSavingRider] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const row = await tablesDB.getRow({
          databaseId: DB_ID,
          tableId: ORDERS_TABLE,
          rowId: orderId,
        });
        setOrder({ ...row, id: row.$id });
        setRider({ name: row.riderName || "", phone: row.riderPhone || "" });

        const cartRes = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: CART_TABLE,
          queries: [Query.equal("orders", row.$id)],
        });
        const cartTableItems = cartRes.rows || cartRes.documents || [];
        // Checkout stores cart items as JSON strings in order.cart - fall back to that
        const parsedOrderCart = (row.cart || []).map((item) => {
          if (typeof item === "string") {
            try {
              return JSON.parse(item);
            } catch {
              return {};
            }
          }
          return item;
        });
        setCartItems(
          cartTableItems.length > 0 ? cartTableItems : parsedOrderCart,
        );
      } catch (e) {
        console.error(e);
        toast.error("Failed to load order");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  const STATUS_ORDER = [
    "pending",
    "processing",
    "out-for-delivery",
    "delivered",
  ];

  const handleStatusChange = async (newStatus) => {
    const currentStatus = order?.fulfillmentStatus || "pending";

    // Block going back once out-for-delivery
    const currentIdx = STATUS_ORDER.indexOf(currentStatus);
    const newIdx = STATUS_ORDER.indexOf(newStatus);
    if (
      currentIdx >= STATUS_ORDER.indexOf("out-for-delivery") &&
      newIdx < STATUS_ORDER.indexOf("out-for-delivery")
    ) {
      toast.warn("Cannot go back once order is out for delivery.");
      return;
    }

    // Require rider info before setting out-for-delivery
    if (
      newStatus === "out-for-delivery" &&
      (!rider.name.trim() || !rider.phone.trim())
    ) {
      toast.warn(
        "Enter rider name and phone number before marking as Out for Delivery.",
      );
      return;
    }

    if (updating) return;
    setUpdating(true);
    try {
      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: ORDERS_TABLE,
        rowId: orderId,
        data: { fulfillmentStatus: newStatus },
      });
      setOrder((prev) => ({ ...prev, fulfillmentStatus: newStatus }));
      toast.success("Status updated");

      const buyer = parseInfoArray(order.shippingInfo);
      if (buyer.email) {
        if (newStatus === "out-for-delivery") {
          fetch("https://email-api.trendbayds.com/send", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "dispatch_notification",
              payload: {
                buyerEmail: buyer.email,
                buyerName: buyer.fullName || "Customer",
                orderNumber: order.orderNumber,
                transactionRef:
                  order.paymentReference?.reference || order.paymentReference,
                riderName: order.riderName || rider.name || "",
                riderPhone: order.riderPhone || rider.phone || "",
                items: cartItems.map((i) => ({
                  title: i.title || i.productName,
                  quantity: i.quantity || 1,
                })),
              },
            }),
          }).catch(() => {});
        } else if (newStatus === "delivered") {
          fetch("https://email-api.trendbayds.com/send", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "order_delivered",
              payload: {
                buyerEmail: buyer.email,
                buyerName: buyer.fullName || "Customer",
                orderNumber: order.orderNumber,
                transactionRef:
                  order.paymentReference?.reference || order.paymentReference,
                items: cartItems.map((i) => ({
                  title: i.title || i.productName,
                  quantity: i.quantity || 1,
                })),
              },
            }),
          }).catch(() => {});
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveRider = async () => {
    if (savingRider) return;
    setSavingRider(true);
    try {
      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: ORDERS_TABLE,
        rowId: orderId,
        data: {
          riderName: rider.name,
          riderPhone: rider.phone,
          fulfillmentStatus: "out-for-delivery",
        },
      });
      setOrder((prev) => ({
        ...prev,
        riderName: rider.name,
        riderPhone: rider.phone,
        fulfillmentStatus: "out-for-delivery",
      }));
      toast.success("Rider info saved · Status set to Out for Delivery");

      // Fire dispatch notification email
      const WORKER = "https://email-api.trendbayds.com";
      if (WORKER) {
        const buyer = parseInfoArray(order.shippingInfo);
        if (buyer.email) {
          fetch(`${WORKER}/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "dispatch_notification",
              payload: {
                buyerEmail: buyer.email,
                buyerName: buyer.fullName || "Customer",
                orderNumber: order.orderNumber,
                riderName: rider.name,
                riderPhone: rider.phone,
                items: cartItems.map((i) => ({
                  title: i.title || i.productName,
                  quantity: i.quantity || 1,
                })),
              },
            }),
          }).catch(() => {});
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to save rider info");
    } finally {
      setSavingRider(false);
    }
  };

  if (loading) {
    return (
      <div className="det-page">
        <p className="det-loading">Loading order…</p>
      </div>
    );
  }

  if (!order) return null;

  const shippingInfo = parseInfoArray(order.shippingInfo);
  const billingInfo = parseInfoArray(order.billingInfo);
  const paid = (order.paymentStatus || "").toLowerCase() === "paid";
  const currentStatus = order.fulfillmentStatus || "pending";

  return (
    <div className="det-page">
      {/* Header */}
      <div className="det-header">
        <button className="det-back" onClick={() => navigate("/d/orders")}>
          <ArrowLeft size={16} /> Back to Orders
        </button>
        <div className="det-header-right">
          <h2 className="det-title">Order #{order.orderNumber || "-"}</h2>
          <p className="det-date">{fmtDate(order.$createdAt)}</p>
        </div>
      </div>

      <div className="det-body">
        {/* LEFT col */}
        <div className="det-left">
          {/* Items */}
          <div className="det-card">
            <h3 className="det-card-title">Items</h3>
            {cartItems.length === 0 ? (
              <p className="det-no-items">No items found</p>
            ) : (
              <div className="det-items">
                {cartItems.map((item, i) => (
                  <div key={i} className="det-item">
                    {(item.image || item.productImage) && (
                      <img
                        className="det-item-img"
                        src={item.image || item.productImage}
                        alt={item.title || item.productName}
                      />
                    )}
                    <div className="det-item-info">
                      <p className="det-item-name">{item.title || "-"}</p>
                      <p className="det-item-qty">Qty: {item.quantity}</p>
                    </div>
                    <p className="det-item-price">{fmtPrice(item.price)}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="det-summary">
              <div className="det-summary-row">
                <span>Total</span>
                <strong>{fmtPrice(order.orderTotal)}</strong>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="det-card">
            <h3 className="det-card-title">Customer</h3>
            <div className="det-info-grid">
              <div>
                <p className="det-info-label">Name</p>
                <p className="det-info-val">{shippingInfo.fullName || "-"}</p>
              </div>
              <div>
                <p className="det-info-label">Email</p>
                <p className="det-info-val">{shippingInfo.email || "-"}</p>
              </div>
              <div>
                <p className="det-info-label">Phone</p>
                <p className="det-info-val">
                  {shippingInfo.phoneNumber || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="det-card det-addr-row">
            <div>
              <h3 className="det-card-title">Shipping Address</h3>
              <p className="det-addr">{shippingInfo.address || "-"}</p>
              <p className="det-addr">
                {[shippingInfo.city, shippingInfo.state]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              <p className="det-addr">{shippingInfo.country || ""}</p>
            </div>
            <div>
              <h3 className="det-card-title">Billing Address</h3>
              <p className="det-addr">{billingInfo.fullName || "-"}</p>
              <p className="det-addr">{billingInfo.address || "-"}</p>
              <p className="det-addr">
                {[billingInfo.city, billingInfo.state]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              <p className="det-addr">{billingInfo.country || ""}</p>
            </div>
          </div>
        </div>

        {/* RIGHT col */}
        <div className="det-right">
          {/* Payment */}
          <div className="det-card">
            <h3 className="det-card-title">Payment</h3>
            <span
              className={`det-pay-badge ${paid ? "det-pay--paid" : "det-pay--unpaid"}`}
            >
              {paid ? "Paid" : order.paymentStatus || "Pending"}
            </span>
            <p className="det-ref">
              {order.transactionRef ? `Ref: ${order.transactionRef}` : ""}
            </p>
          </div>

          {/* Fulfillment */}
          <div className="det-card">
            <h3 className="det-card-title">Fulfillment Status</h3>
            <div className="det-status-list">
              {STATUSES.map(({ value, label, icon: Icon, cls }) => {
                const currentIdx = STATUS_ORDER.indexOf(currentStatus);
                const thisIdx = STATUS_ORDER.indexOf(value);
                const isLocked =
                  currentIdx >= STATUS_ORDER.indexOf("out-for-delivery") &&
                  thisIdx < STATUS_ORDER.indexOf("out-for-delivery");
                return (
                  <button
                    key={value}
                    className={`det-status-btn ${cls} ${currentStatus === value ? "det-status-btn--active" : ""}`}
                    onClick={() => handleStatusChange(value)}
                    disabled={updating || isLocked}
                    style={
                      isLocked ? { opacity: 0.35, cursor: "not-allowed" } : {}
                    }
                  >
                    <Icon size={14} />
                    {label}
                    {currentStatus === value && (
                      <span className="det-status-check">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rider Info */}
          <div className="det-card">
            <h3 className="det-card-title">Rider Info</h3>
            <div className="det-rider-field">
              <label className="det-rider-label">
                <User size={12} /> Rider Name
              </label>
              <input
                className="det-rider-input"
                type="text"
                placeholder="e.g. Emeka Okafor"
                value={rider.name}
                onChange={(e) =>
                  setRider((r) => ({ ...r, name: e.target.value }))
                }
              />
            </div>
            <div className="det-rider-field">
              <label className="det-rider-label">
                <Phone size={12} /> Phone Number
              </label>
              <input
                className="det-rider-input"
                type="tel"
                placeholder="e.g. 08012345678"
                value={rider.phone}
                onChange={(e) =>
                  setRider((r) => ({ ...r, phone: e.target.value }))
                }
              />
            </div>
            <button
              className="det-rider-save"
              onClick={handleSaveRider}
              disabled={savingRider}
            >
              <Save size={13} />
              {savingRider ? "Saving…" : "Save Rider Info"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
